// useNodeSidebarRail.js

import { useEffect } from 'react';

import { useUI } from 'context/UIContext';
import { useIsMobile } from 'hooks/useDeviceDetect';
import { TABLET_SHELL_MAX_WIDTH_PX } from 'utilities/layoutBreakpoints';

/** Viewport width at/below which the desktop menu stays icon-rail compact (iPad). */
export const SIDEBAR_RAIL_COMPACT_MAX_WIDTH = TABLET_SHELL_MAX_WIDTH_PX;

/**
 * useNodeSidebarRail
 *
 * Force-collapses the desktop left menu to an icon rail when the viewport is
 * at or below TABLET_SHELL_MAX_WIDTH_PX — expand cannot override that.
 * Collapse / expand on wider screens is manual only (logo-row toggle).
 * Mobile drawer is unchanged.
 */
export default function useNodeSidebarRail() {
  const isMobile = useIsMobile();
  const {
    setSidebarRailCompact,
    setSidebarRailNarrowViewport,
  } = useUI();

  // Track narrow viewports (iPad / small desktop). Narrow always wins over expand.
  useEffect(() => {
    if (isMobile) {
      setSidebarRailNarrowViewport(false);
      setSidebarRailCompact(false);
      return undefined;
    }

    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }

    const mediaQuery = window.matchMedia(
      `(max-width: ${SIDEBAR_RAIL_COMPACT_MAX_WIDTH}px)`,
    );

    const applyViewport = () => {
      setSidebarRailNarrowViewport(mediaQuery.matches);
    };

    applyViewport();

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', applyViewport);
      return () => mediaQuery.removeEventListener('change', applyViewport);
    }

    mediaQuery.addListener(applyViewport);
    return () => mediaQuery.removeListener(applyViewport);
  }, [isMobile, setSidebarRailCompact, setSidebarRailNarrowViewport]);
}
